import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { LegalDisclaimer } from "../components/LegalDisclaimer";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Compass,
  Crown,
  ArrowLeft,
  Sparkle,
  CheckCircle,
  Lightning,
} from "@phosphor-icons/react";

const SAMPLE = "Our mission is to empower underserved women in South Carolina through storytelling, mentorship, and faith-based community building, equipping them to walk in their God-given destiny.";

const SCORE_LABELS = {
  clarity: "Clarity",
  impact_verbs: "Impact Verbs",
  specificity: "Specificity",
  funder_readiness: "Funder Readiness",
};

const scoreColor = (n) => {
  if (n >= 80) return "text-emerald-400";
  if (n >= 60) return "text-amber-400";
  return "text-rose-400";
};
const scoreBg = (n) => {
  if (n >= 80) return "bg-emerald-500/15 border-emerald-500/40";
  if (n >= 60) return "bg-amber-500/15 border-amber-500/40";
  return "bg-rose-500/15 border-rose-500/40";
};

const MissionDiagnostic = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mission, setMission] = useState("");
  const [orgContext, setOrgContext] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [savedKey, setSavedKey] = useState(null); // which rewrite the user saved as their profile mission
  const [upgradePrompt, setUpgradePrompt] = useState(null); // {previous_score, message} when free-run is used

  const saveAsMission = async (key, text) => {
    if (!text || text.trim().length < 10) {
      toast.error("Rewrite is too short to save");
      return;
    }
    try {
      await axios.patch(`${API}/profile/mission`, { mission: text });
      setSavedKey(key);
      toast.success("Saved to your profile — Flash Drafts will use this version");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't save mission";
      toast.error(detail);
    }
  };

  const runDiagnostic = async () => {
    if (mission.trim().length < 10) {
      toast.error("Please paste your mission statement (10+ chars).");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/formation/mission-diagnostic`, {
        mission,
        org_context: orgContext,
      });
      setResult(res.data);
      window.scrollTo({ top: 400, behavior: "smooth" });
    } catch (err) {
      if (err.response?.status === 402) {
        const detail = err.response?.data?.detail;
        // Backend now returns structured detail for the free-run-used case
        if (detail && typeof detail === "object" && detail.code === "free_run_used") {
          setUpgradePrompt({
            previousScore: detail.previous_score,
            message: detail.message,
          });
        } else {
          // Legacy / other 402 — fall back to a basic toast
          toast.error(typeof detail === "string" ? detail : "Upgrade for unlimited Mission Diagnostics.");
        }
      } else {
        toast.error(err.response?.data?.detail || "Something went wrong. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (upgradePrompt) {
    return (
      <DashboardLayout>
        <div className="max-w-3xl mx-auto px-6 py-12" data-testid="md-upgrade-prompt">
          <button onClick={() => navigate(-1)} className="text-zinc-500 hover:text-emerald-400 text-sm flex items-center gap-1 mb-6">
            <ArrowLeft size={14} /> Back
          </button>
          <Card className="bg-zinc-950 border-emerald-500/30">
            <CardContent className="p-10 text-center">
              <Crown weight="fill" size={48} className="mx-auto text-emerald-400 mb-5" />
              {upgradePrompt.previousScore != null && (
                <div className="mb-5">
                  <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Your last score</div>
                  <div className={`text-5xl font-black ${scoreColor(upgradePrompt.previousScore)}`} data-testid="md-previous-score">
                    {upgradePrompt.previousScore}<span className="text-2xl text-zinc-600">/100</span>
                  </div>
                </div>
              )}
              <h1 className="text-3xl font-black mb-3">Want to iterate?</h1>
              <p className="text-zinc-400 mb-6 leading-relaxed">
                {upgradePrompt.message}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => navigate("/pricing")}
                  className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold px-6 py-5"
                  data-testid="md-upgrade-btn"
                >
                  Upgrade to Founding Member
                </Button>
                <Button
                  onClick={() => navigate("/grants")}
                  variant="outline"
                  className="border-zinc-700 text-zinc-300 hover:text-white px-6 py-5"
                  data-testid="md-browse-grants-btn"
                >
                  Browse matched grants
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-6 py-10" data-testid="mission-diagnostic-page">
        <button onClick={() => navigate(-1)} className="text-zinc-500 hover:text-emerald-400 text-sm flex items-center gap-1 mb-6" data-testid="back-btn">
          <ArrowLeft size={14} /> Back
        </button>

        <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-4">
          <Compass weight="fill" size={12} className="text-emerald-400" />
          <span className="text-[10px] font-bold tracking-[0.2em] text-emerald-300 uppercase">
            Formation Toolkit · Module 01
          </span>
        </div>

        <h1 className="text-4xl sm:text-5xl font-black tracking-tighter mb-4">
          Mission Statement <span className="text-emerald-400">Diagnostic</span>
        </h1>
        <p className="text-zinc-400 text-lg leading-relaxed mb-10 max-w-2xl">
          Paste your draft. Get a 0–100 score on clarity, impact verbs, specificity, and funder readiness — plus three rewrites tuned to South Carolina funders.
        </p>

        {/* Input */}
        <Card className="bg-zinc-950 border-zinc-800 mb-8">
          <CardContent className="p-6 space-y-5">
            <div>
              <Label className="text-sm text-zinc-300">Your draft mission statement *</Label>
              <Textarea
                value={mission}
                onChange={(e) => setMission(e.target.value)}
                rows={5}
                maxLength={1500}
                className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-2 resize-none"
                placeholder={`e.g. "${SAMPLE}"`}
                data-testid="mission-input"
              />
              <div className="text-xs text-zinc-500 mt-1.5 text-right">{mission.length}/1500</div>
            </div>
            <div>
              <Label className="text-sm text-zinc-300">Quick context about your org <span className="text-zinc-600 text-xs">(optional — helps the AI calibrate)</span></Label>
              <Textarea
                value={orgContext}
                onChange={(e) => setOrgContext(e.target.value)}
                rows={2}
                maxLength={500}
                className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-2 resize-none"
                placeholder="e.g. SC nonprofit serving teen girls in Horry County; ~3 yrs old; recently incorporated"
                data-testid="org-context-input"
              />
            </div>
            <Button
              onClick={runDiagnostic}
              disabled={loading || mission.trim().length < 10}
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold py-6 text-base"
              data-testid="run-diagnostic-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Sparkle weight="fill" className="w-4 h-4 animate-pulse" />
                  Analyzing your mission…
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Lightning weight="fill" className="w-4 h-4" />
                  Run Diagnostic
                </span>
              )}
            </Button>
            <p className="text-[11px] text-zinc-600 text-center">
              Powered by GPT-4o. Each run takes ~15-25 seconds. Re-run as many times as you want.
            </p>
          </CardContent>
        </Card>

        {/* Result */}
        {result && (
          <div className="space-y-6" data-testid="diagnostic-result">
            {/* Overall score */}
            <Card className={`border ${scoreBg(result.overall_score)}`}>
              <CardContent className="p-7 flex items-center gap-6">
                <div className={`text-7xl font-black tracking-tighter ${scoreColor(result.overall_score)}`} data-testid="overall-score">
                  {result.overall_score}
                </div>
                <div className="flex-1">
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-400 mb-1">Overall Score</div>
                  <div className="text-xl font-bold text-white mb-2">
                    {result.overall_score >= 80 && "Strong — ready to take into funder conversations."}
                    {result.overall_score >= 60 && result.overall_score < 80 && "Solid foundation — a few sharpenings will lift this significantly."}
                    {result.overall_score < 60 && "Room to grow — let's sharpen the angles below."}
                  </div>
                  <div className="text-sm text-zinc-300 italic">
                    <Sparkle weight="fill" className="inline w-3 h-3 text-emerald-400 mr-1" />
                    {result.top_recommendation}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Sub-scores */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {Object.entries(result.scores || {}).map(([k, v]) => (
                <Card key={k} className="bg-zinc-950 border-zinc-800">
                  <CardContent className="p-5 text-center">
                    <div className={`text-4xl font-black tracking-tight ${scoreColor(v)}`}>{v}</div>
                    <div className="text-[10px] tracking-wider uppercase font-bold text-zinc-500 mt-1">
                      {SCORE_LABELS[k] || k}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Coaching */}
            <Card className="bg-zinc-950 border-zinc-800">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-4">Coaching breakdown</h2>
                <div className="space-y-4">
                  {Object.entries(result.coaching || {}).map(([k, v]) => (
                    <div key={k} className="border-l-2 border-emerald-500/40 pl-4">
                      <div className="text-[11px] tracking-wider uppercase font-bold text-emerald-300 mb-1">
                        {SCORE_LABELS[k] || k} · {result.scores?.[k] ?? "—"}/100
                      </div>
                      <p className="text-sm text-zinc-300 leading-relaxed">{v}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Rewrites */}
            <Card className="bg-zinc-950 border-emerald-500/30">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-1 flex items-center gap-2">
                  <CheckCircle weight="fill" className="text-emerald-400 w-5 h-5" />
                  Three rewrites
                </h2>
                <p className="text-xs text-zinc-500 mb-5">Tuned to how SC funders describe what they fund. Edit freely.</p>

                <div className="space-y-5">
                  {[
                    { key: "one_line", label: "One Line", hint: "Use this in your email signature, your elevator pitch, and at the top of your About page." },
                    { key: "one_paragraph", label: "One Paragraph", hint: "Use this in grant 'About Your Org' sections and your website hero." },
                    { key: "one_page_opener", label: "One-Page Opener", hint: "Use this as the opening of longer grant application narratives." },
                  ].map(({ key, label, hint }) => {
                    const text = result.rewrites?.[key] || "";
                    const isSaved = savedKey === key;
                    return (
                      <div key={key}>
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="text-[11px] tracking-wider uppercase font-bold text-emerald-300">{label}</div>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => saveAsMission(key, text)}
                              disabled={isSaved || !text}
                              className={`text-[11px] font-semibold transition-colors ${
                                isSaved
                                  ? "text-emerald-400 cursor-default"
                                  : "text-emerald-300 hover:text-emerald-200"
                              }`}
                              data-testid={`save-as-mission-${key}`}
                            >
                              {isSaved ? "✓ Saved to profile" : "Save as my mission"}
                            </button>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(text);
                                toast.success(`${label} copied`);
                              }}
                              className="text-[11px] text-zinc-500 hover:text-emerald-400"
                              data-testid={`copy-${key}`}
                            >
                              Copy
                            </button>
                          </div>
                        </div>
                        <div className="bg-zinc-900 border border-zinc-800 rounded p-4 text-sm text-zinc-100 leading-relaxed whitespace-pre-wrap">
                          {text || "—"}
                        </div>
                        <p className="text-[11px] text-zinc-600 mt-1.5 italic">{hint}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Post-save next-step card — only renders after a rewrite is promoted to profile */}
            {savedKey && (
              <Card
                className="bg-gradient-to-br from-emerald-500/10 via-zinc-950 to-zinc-950 border-emerald-500/40 border-2"
                data-testid="post-save-next-step"
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 w-12 h-12 rounded-sm bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
                      <Sparkle weight="duotone" className="w-6 h-6 text-emerald-400" />
                    </div>
                    <div className="flex-1">
                      <div className="text-[10px] text-emerald-300 font-bold uppercase tracking-[0.18em] mb-1">
                        Your mission is now live
                      </div>
                      <h3 className="text-lg font-bold text-white leading-tight mb-1.5">
                        Let&apos;s see which grants your sharper mission unlocks.
                      </h3>
                      <p className="text-sm text-zinc-400 mb-4">
                        Your Best Match scores are recalculated against the new mission. Stronger language = better matches = better Flash Drafts.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Button
                          onClick={() => navigate("/grants")}
                          className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold"
                          data-testid="post-save-see-matches-btn"
                        >
                          See my refreshed matches →
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => navigate("/profile")}
                          className="border-zinc-700 text-zinc-200 hover:bg-zinc-900"
                          data-testid="post-save-view-profile-btn"
                        >
                          View profile
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="text-center">
              <Button
                variant="outline"
                onClick={() => { setResult(null); setMission(""); setOrgContext(""); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                className="border-zinc-700 text-zinc-200 hover:bg-zinc-900"
                data-testid="run-again-btn"
              >
                Run another diagnostic
              </Button>
            </div>
          </div>
        )}
        <LegalDisclaimer variant="footer" />
      </div>
    </DashboardLayout>
  );
};

export default MissionDiagnostic;
